/**
 * API client. Wraps fetch with the standard error envelope and centralizes
 * the per-resource calls.
 *
 * In dev, the Vite proxy forwards `/v1/*` to the API on port 8787.
 */
import type {
  TaskSummary,
  TaskDetail,
  TaskCreate,
  TaskEdit,
  CancelTaskInput,
  RetryTaskInput,
  ReopenTaskInput,
  ContextRecord,
  ContextInput,
  TaskEventRecord,
  PendingInputRecord,
  AuditActionRecord,
  Profile,
  PauseReason,
  ApiError,
  TaskState,
  Domain,
  DomainCreate,
  DomainEdit,
} from '@agentflow/shared';

export interface DomainRecord extends Domain {
  created_at: string;
  updated_at: string;
}

// API base URL.
//
// Default behavior is **same-origin** — the SPA and the Worker are served
// from the same hostname (`agentflow.wpscholar.com` in production), with
// Worker routes catching `/v1/*` and `/health` and Pages serving everything
// else. Same-origin means: no CORS, single Access cookie, simpler deploys.
//
// `VITE_API_URL` is an optional override — set it only when you intentionally
// want to call a different host (e.g. the workers.dev URL during a debugging
// session, or a separate API deploy). Leave it unset for normal use.
//
// In dev, the Vite dev-server proxy (vite.config.ts) forwards `/v1/*` to the
// local Worker on :8787, so same-origin works locally too.
const BASE: string = (() => {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv.replace(/\/+$/, '');
  }
  return '';
})();

class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    credentials: 'include',
  };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(`${BASE}${path}`, init);
  if (res.status === 204) return undefined as T;

  const data = (await res.json()) as T | ApiError;
  if (!res.ok) {
    const err = data as ApiError;
    throw new ApiClientError(
      res.status,
      err.error?.code ?? 'unknown',
      err.error?.message ?? `Request failed with ${res.status}`,
      err.error?.details,
    );
  }
  return data as T;
}

export interface ListTasksOptions {
  state?: TaskState[];
  domain?: string;
  owner_id?: string;
  assignee_id?: string;
  limit?: number;
}

export const api = {
  me: () => request<{ user: { id: string; name: string } }>('GET', '/v1/me'),

  listTasks: (opts: ListTasksOptions = {}) => {
    const params = new URLSearchParams();
    if (opts.state?.length) params.set('state', opts.state.join(','));
    if (opts.domain) params.set('domain', opts.domain);
    if (opts.owner_id) params.set('owner_id', opts.owner_id);
    if (opts.assignee_id) params.set('assignee_id', opts.assignee_id);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request<{ tasks: TaskSummary[] }>(
      'GET',
      `/v1/tasks${qs ? `?${qs}` : ''}`,
    );
  },

  getTask: (id: string) => request<{ task: TaskDetail }>('GET', `/v1/tasks/${id}`),

  createTask: (input: TaskCreate) =>
    request<{ task: TaskSummary }>('POST', '/v1/tasks', input),

  editTask: (id: string, input: TaskEdit) =>
    request<{ task: TaskSummary }>('PATCH', `/v1/tasks/${id}`, input),

  deleteTask: (id: string, reason?: string) =>
    request<void>(
      'DELETE',
      `/v1/tasks/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
    ),

  completeWork: (id: string) =>
    request<{ task: TaskSummary }>('POST', `/v1/tasks/${id}/complete`),

  cancelTask: (id: string, input: CancelTaskInput = {}) =>
    request<{ task: TaskSummary }>('POST', `/v1/tasks/${id}/cancel`, input),

  retryTask: (id: string, input: RetryTaskInput) =>
    request<{ task: TaskSummary }>('POST', `/v1/tasks/${id}/retry`, input),

  reopenTask: (id: string, input: ReopenTaskInput) =>
    request<{ task: TaskSummary }>('POST', `/v1/tasks/${id}/reopen`, input),

  failTask: (id: string, input: { reason: string; details?: string }) =>
    request<{ task: TaskSummary }>('POST', `/v1/tasks/${id}/fail`, input),

  openInterrupt: (
    id: string,
    input: { reason: 'clarification' | 'guardrail'; payload: Record<string, unknown> },
  ) => request<{ task: TaskSummary }>('POST', `/v1/tasks/${id}/open-pause`, input),

  addContext: (taskId: string, input: ContextInput) =>
    request<{ context: ContextRecord }>('POST', `/v1/context/${taskId}`, input),

  removeContext: (taskId: string, contextId: string) =>
    request<void>('DELETE', `/v1/context/${taskId}/${contextId}`),

  listEvents: (taskId?: string, limit = 100) => {
    const params = new URLSearchParams();
    if (taskId) params.set('task_id', taskId);
    params.set('limit', String(limit));
    return request<{ events: TaskEventRecord[] }>('GET', `/v1/events?${params}`);
  },

  listProfiles: () => request<{ profiles: Profile[] }>('GET', '/v1/profiles'),

  listDomains: () => request<{ domains: DomainRecord[] }>('GET', '/v1/domains'),

  createDomain: (input: DomainCreate) =>
    request<{ domain: DomainRecord }>('POST', '/v1/domains', input),

  editDomain: (slug: string, input: DomainEdit) =>
    request<{ domain: DomainRecord }>('PATCH', `/v1/domains/${slug}`, input),

  deleteDomain: (slug: string) => request<void>('DELETE', `/v1/domains/${slug}`),

  listAudit: (limit = 100) =>
    request<{ actions: AuditActionRecord[] }>('GET', `/v1/audit?limit=${limit}`),

  listPending: (assigneeId?: string, reason?: PauseReason) => {
    const params = new URLSearchParams();
    if (assigneeId) params.set('assignee_id', assigneeId);
    if (reason) params.set('reason', reason);
    return request<{
      pending: { pending: PendingInputRecord; task: TaskSummary }[];
    }>('GET', `/v1/pauses${params.toString() ? `?${params}` : ''}`);
  },

  resolvePause: (taskId: string, reason: PauseReason, resolution: Record<string, unknown>) =>
    request<{ task: TaskSummary }>('POST', `/v1/pauses/${taskId}/resolve`, {
      reason,
      resolution,
    }),
};

export { ApiClientError };
